import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Actions } from "flexlayout-react";

import { Widget } from "fond/types";

import { LayoutContext } from "../LayoutProvider";

/**
 * Registry mapping tab IDs to callbacks that receive the portal target DOM element.
 * Used to bridge between the SubLayoutPopout component and the PopoutContent widget.
 */
const portalTargetCallbacks = new Map<string, (el: HTMLElement | null) => void>();

/**
 * Called by the PopoutContent widget to register its root DOM element as a portal target.
 */
export const registerPortalTarget = (tabId: string, el: HTMLElement | null): void => {
  portalTargetCallbacks.get(tabId)?.(el);
};

/**
 * FlexLayout subtracts these from the rect to determine the tab content size.
 */
const OFFSET_WIDTH = 12;
const OFFSET_HEIGHT = 23;
const DEFAULT_X_COORDINATE = 50;
const DEFAULT_Y_COORDINATE = 50;
const DEFAULT_WIDTH = 200;
const DEFAULT_HEIGHT = 50;
interface SubLayoutPopoutProps {
  /**
   * The ID of the tab to create in the sub-layout. Must be unique across all tabs in the layout.
   */
  tabId: string;
  /**
   * If false, the popout is not created and children are not rendered.
   * Popout re-opens at the last saved position and size when `open` is set to true again.
   */
  open?: boolean;
  /**
   * If provided, the popout is created at this position and size. X and Y are relative to the top-left of the map sub-layout (including the tab strip).
   */
  rect?: { x: number; y: number; width?: number; height?: number };
  /**
   * The content to render in the popout
   */
  children: React.ReactNode;
}

/**
 * Creates a floating popout panel in the map sub-layout on mount and removes it on unmount.
 * Children are rendered into the popout via a React portal, preserving the parent's React context.
 *
 * If `rect.height` is omitted, the content is measured in a hidden container before the popout
 * is created so it opens at the correct height. Similarly for `rect.width`.
 */
const SubLayoutPopout: React.FC<SubLayoutPopoutProps> = ({ tabId, open = true, rect: rectProp, children }) => {
  const { x = DEFAULT_X_COORDINATE, y = DEFAULT_Y_COORDINATE, width, height } = rectProp ?? {};
  const dynamicHeight = height == null;
  const dynamicWidth = width == null;
  const dynamicSizing = dynamicHeight || dynamicWidth;
  const layout = useContext(LayoutContext);
  const mapModelRef = useRef(layout?.mapModel);
  mapModelRef.current = layout?.mapModel;
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const [measuredSize, setMeasuredSize] = useState<{ width: number; height: number } | null>(null);
  const lastRectRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Reset measured size when the popout closes so dynamic dimensions are
  // re-measured on the next open, picking up any content size changes.
  useEffect(() => {
    if (!open && dynamicSizing) {
      setMeasuredSize(null);
    }
  }, [open, dynamicSizing]);

  // Phase 1: Measure content rendered in a hidden container so the popout
  // can be created at the correct size from the start. useLayoutEffect fires
  // before the browser paints, so the hidden container is never visible.
  useLayoutEffect(() => {
    if (!open || !dynamicSizing || measuredSize || !measureRef.current) return;

    const contentRect = measureRef.current.getBoundingClientRect();
    const contentWidth = Math.ceil(contentRect.width);
    const contentHeight = Math.ceil(contentRect.height);

    if (contentWidth === 0 && contentHeight === 0) return;

    setMeasuredSize({
      width: dynamicWidth ? contentWidth + OFFSET_WIDTH : (width ?? DEFAULT_WIDTH),
      height: dynamicHeight ? contentHeight + OFFSET_HEIGHT : (height ?? DEFAULT_HEIGHT),
    });
  }, [open, dynamicSizing, dynamicWidth, dynamicHeight, measuredSize, width, height]);

  // Determine final popout dimensions once measurement is complete.
  const popoutWidth = dynamicWidth ? (measuredSize?.width ?? null) : (width ?? DEFAULT_WIDTH);
  const popoutHeight = dynamicHeight ? (measuredSize?.height ?? null) : (height ?? DEFAULT_HEIGHT);

  // Phase 2: Create the popout once dimensions are known and open is true.
  // Remove the popout when closed or unmounted, saving its position for later re-open.
  useEffect(() => {
    if (!open || popoutWidth == null || popoutHeight == null) return undefined;

    portalTargetCallbacks.set(tabId, setPortalTarget);

    const model = mapModelRef.current;
    if (model && !model.getNodeById(tabId)) {
      const saved = lastRectRef.current;
      const rect = saved
        ? {
            x: saved.x,
            y: saved.y,
            width: dynamicWidth ? popoutWidth : saved.width,
            height: dynamicHeight ? popoutHeight : saved.height,
          }
        : { x, y, width: popoutWidth, height: popoutHeight };
      model.doAction(
        Actions.createPopout(
          {
            type: "row",
            children: [
              {
                type: "tabset",
                enableMaximize: false,
                enableTabStrip: false,
                children: [{ type: "tab", id: tabId, component: Widget.PopoutContent }],
              },
            ],
          },
          rect,
          "float"
        )
      );
    }

    return () => {
      portalTargetCallbacks.delete(tabId);
      setPortalTarget(null);
      const m = mapModelRef.current;
      const node = m?.getNodeById(tabId);
      if (node && m) {
        const layoutId = node.getLayoutId();
        const savedRect = m.toJson().subLayouts?.[layoutId]?.rect;
        if (savedRect) {
          lastRectRef.current = savedRect;
        }
        m.doAction(Actions.deleteTab(tabId));
      }
    };
  }, [tabId, x, y, popoutWidth, popoutHeight, open, layout?.mapModel]);

  if (!open) return null;

  // While measuring, render children in a hidden container.
  if (dynamicSizing && !measuredSize) {
    return (
      <div
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          ...(dynamicWidth ? { width: "max-content" } : {}),
        }}
      >
        {children}
      </div>
    );
  }

  if (!portalTarget) return null;

  return createPortal(children, portalTarget);
};

export default SubLayoutPopout;
